import { useMemo } from 'react';
import { BellRing, ClipboardCheck, GitMerge, History, type LucideIcon, Ticket } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { usePreferences } from '../preferences/PreferencesProvider';
import { NAVIGATION } from './registry';

/**
 * A searchable destination for the command palette.
 *
 * The palette's navigation index is every place a user can go — the sidebar
 * destinations *and* the tab pages that were folded out of the sidebar in Phase 2.2
 * (so search still reaches Enrollment, Pending, Conflicts, Rules, Revisions). Each
 * carries a breadcrumb for context and the same permission/mode gates as the sidebar,
 * so the palette never offers — or leaks the name of — a page the user cannot open.
 */
export interface Command {
  id: string;
  titleKey: string;
  /** Parent labels (i18n keys), shown as the result's context path. */
  breadcrumbKeys: string[];
  route: string;
  icon: LucideIcon;
  requiredPermissions?: string[];
  advanced?: boolean;
  keywords?: string[];
}

/** Tab pages folded out of the sidebar in Phase 2.2, still reachable via search. */
const TAB_COMMANDS: Command[] = [
  {
    id: 'cmd-enrollment',
    titleKey: 'nav.tabs.enrollment',
    breadcrumbKeys: ['nav.groups.network', 'nav.devices'],
    route: '/devices/enrollment',
    icon: Ticket,
    requiredPermissions: ['edge_device:write'],
    keywords: ['token', 'onboard', 'claim'],
  },
  {
    id: 'cmd-pending',
    titleKey: 'nav.tabs.pending',
    breadcrumbKeys: ['nav.groups.network', 'nav.devices'],
    route: '/devices/pending',
    icon: ClipboardCheck,
    requiredPermissions: ['edge_device:write'],
    keywords: ['registration', 'approve', 'pending'],
  },
  {
    id: 'cmd-conflicts',
    titleKey: 'nav.tabs.conflicts',
    breadcrumbKeys: ['nav.groups.network', 'nav.devices'],
    route: '/devices/conflicts',
    icon: GitMerge,
    requiredPermissions: ['edge_device:write'],
    keywords: ['adopt', 'ownership'],
  },
  {
    id: 'cmd-alert-rules',
    titleKey: 'nav.tabs.rules',
    breadcrumbKeys: ['nav.groups.operations', 'nav.alerts'],
    route: '/alerts/rules',
    icon: BellRing,
    requiredPermissions: ['alert:write'],
    keywords: ['threshold', 'condition', 'alert'],
  },
  {
    id: 'cmd-revisions',
    titleKey: 'nav.tabs.revisions',
    breadcrumbKeys: ['nav.groups.operations', 'nav.deployments'],
    route: '/deployments/revisions',
    icon: History,
    requiredPermissions: ['deployment:read'],
    keywords: ['desired state', 'config', 'version'],
  },
];

/** The full navigation index: sidebar destinations + folded tab pages. */
function buildCommands(): Command[] {
  const fromNav: Command[] = NAVIGATION.flatMap((domain) =>
    domain.items.map((item) => ({
      id: `cmd-${item.id}`,
      titleKey: item.labelKey,
      breadcrumbKeys: [domain.labelKey],
      route: item.route,
      icon: item.icon,
      ...(item.requiredPermissions ? { requiredPermissions: item.requiredPermissions } : {}),
      ...(item.advanced ? { advanced: item.advanced } : {}),
      ...(item.searchKeywords ? { keywords: item.searchKeywords } : {}),
    })),
  );
  return [...fromNav, ...TAB_COMMANDS];
}

/** Commands the current user may run, filtered by permission (mode does not hide search). */
export function useCommands(): Command[] {
  const { can } = useAuth();
  // Palette search intentionally ignores Basic/Advanced mode: search should reach a
  // permitted page even if the sidebar currently hides it behind Advanced.
  usePreferences();
  return useMemo(
    () =>
      buildCommands().filter(
        (c) => !c.requiredPermissions || c.requiredPermissions.some((p) => can(p)),
      ),
    [can],
  );
}
